import React, { useState, useEffect } from "react";
import { X, Camera, Plus, Trash2 } from "lucide-react";
import {
  CreateIQWatchDeviceParams,
  UpdateIQWatchDeviceParams,
  IQWatchDevice,
  Camera as CameraType,
} from "../../types";
import Select from "react-select";
import messageService from "../Message/message.ts";
import {
  syncAllDevices,
  getIQWatchDevicesBySite,
} from "../../services/iqwatchService.ts";
import {
  cameraService,
  handleCameraOperation,
} from "../../services/cameraService.ts";

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET) - America/New_York" },
  { value: "America/Chicago", label: "Central Time (CT) - America/Chicago" },
  { value: "America/Denver", label: "Mountain Time (MT) - America/Denver" },
  {
    value: "America/Los_Angeles",
    label: "Pacific Time (PT) - America/Los_Angeles",
  },
  {
    value: "America/Anchorage",
    label: "Alaska Time (AKT) - America/Anchorage",
  },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST) - Pacific/Honolulu" },
  {
    value: "Europe/London",
    label: "Greenwich Mean Time (GMT) - Europe/London",
  },
  {
    value: "Europe/Paris",
    label: "Central European Time (CET) - Europe/Paris",
  },
  {
    value: "Europe/Berlin",
    label: "Central European Time (CET) - Europe/Berlin",
  },
  { value: "Europe/Moscow", label: "Moscow Time (MSK) - Europe/Moscow" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST) - Asia/Tokyo" },
  {
    value: "Asia/Shanghai",
    label: "China Standard Time (CST) - Asia/Shanghai",
  },
  { value: "Asia/Seoul", label: "Korea Standard Time (KST) - Asia/Seoul" },
  { value: "Asia/Singapore", label: "Singapore Time (SGT) - Asia/Singapore" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST) - Asia/Kolkata" },
  {
    value: "Australia/Sydney",
    label: "Australian Eastern Time (AET) - Australia/Sydney",
  },
  {
    value: "Australia/Perth",
    label: "Australian Western Time (AWT) - Australia/Perth",
  },
  {
    value: "Pacific/Auckland",
    label: "New Zealand Standard Time (NZST) - Pacific/Auckland",
  },
];

interface IQWatchDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateIQWatchDeviceParams | UpdateIQWatchDeviceParams
  ) => Promise<void>;
  device?: IQWatchDevice | null;
  loading?: boolean;
  siteId: string;
  orgId: string;
  onDeviceListRefresh?: () => void;
}

const AddIQWatchDeviceModal: React.FC<IQWatchDeviceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  device,
  loading = false,
  siteId,
  orgId,
  onDeviceListRefresh,
}) => {
  const isEditMode = !!device;

  const [form, setForm] = useState<
    CreateIQWatchDeviceParams & {
      sync_interval_minutes?: number;
      router_serial_number?: string;
    }
  >({
    name: "",
    iqwatch_device_id: "",
    site_id: siteId,
    org_id: orgId,
    timezone: "UTC",
    sync_interval_minutes: 5, // Default sync interval
    router_serial_number: "", // Optional router serial number
  });

  // Camera binding state
  const [cameras, setCameras] = useState<
    Array<{ id: string | null; cloud_id: string }>
  >([{ id: null, cloud_id: "" }]);
  const [existingCameras, setExistingCameras] = useState<CameraType[]>([]);
  const [loadingCameras, setLoadingCameras] = useState(false);

  // Load existing cameras when in edit mode
  const loadExistingCameras = React.useCallback(async () => {
    if (!device || !isOpen) {
      // Reset cameras if not in edit mode or modal not open
      if (!device) {
        setCameras([{ id: null, cloud_id: "" }]);
        setExistingCameras([]);
      }
      return;
    }

    setLoadingCameras(true);
    try {
      const response = await cameraService.getCameras(device.id);
      const camerasList = response.cameras || [];
      setExistingCameras(camerasList);

      // Initialize cameras array with existing cameras
      const camerasFormData: Array<{ id: string | null; cloud_id: string }> =
        camerasList.map((cam) => ({
          id: cam.id,
          cloud_id: cam.cloud_id || "",
        }));

      // Fill up to 4 slots, add empty slots if less than 4
      while (camerasFormData.length < 4) {
        camerasFormData.push({ id: null, cloud_id: "" });
      }

      setCameras(camerasFormData.slice(0, 4));
    } catch (error) {
      console.error("Failed to load existing cameras:", error);
      // If loading fails, just reset to empty state
      setCameras([{ id: null, cloud_id: "" }]);
      setExistingCameras([]);
    } finally {
      setLoadingCameras(false);
    }
  }, [device, isOpen]);

  // Load cameras when modal opens in edit mode
  useEffect(() => {
    if (isOpen && device) {
      loadExistingCameras();
    }
  }, [isOpen, device, loadExistingCameras]);

  useEffect(() => {
    if (device) {
      setForm({
        name: device.name,
        iqwatch_device_id: device.iqwatch_device_id,
        site_id: device.site.id,
        org_id: device.organization.id,
        timezone: device.timezone,
        sync_interval_minutes: device.sync_interval_minutes,
        router_serial_number: device.router_serial_number || "",
      });
      // Load existing cameras in edit mode
      loadExistingCameras();
    } else {
      setForm({
        name: "",
        iqwatch_device_id: "",
        site_id: siteId,
        org_id: orgId,
        timezone: "UTC",
        sync_interval_minutes: 5,
        router_serial_number: "",
      });
      // Reset cameras for new device
      setCameras([{ id: null, cloud_id: "" }]);
      setExistingCameras([]);
    }
  }, [device, siteId, orgId, loadExistingCameras]);

  // Add camera input field
  const addCameraField = () => {
    if (cameras.length < 4) {
      setCameras([...cameras, { id: null, cloud_id: "" }]);
    }
  };

  // Remove camera input field
  const removeCameraField = (index: number) => {
    if (cameras.length > 1) {
      const newCameras = cameras.filter((_, i) => i !== index);
      setCameras(newCameras);
    }
  };

  // Update camera cloud_id
  const updateCameraCloudId = (index: number, cloud_id: string) => {
    const newCameras = [...cameras];
    newCameras[index] = { ...newCameras[index], cloud_id };
    setCameras(newCameras);
  };

  // Sync cameras after device creation/update
  const syncCameras = async (deviceId: string) => {
    try {
      // Get current cameras from form (non-empty cloud_ids)
      const formCameras = cameras
        .map((cam) => cam.cloud_id?.trim())
        .filter((cloud_id) => cloud_id && cloud_id.length > 0);

      // Get existing camera cloud_ids
      const existingCloudIds = existingCameras
        .map((cam) => cam.cloud_id)
        .filter((cloud_id) => cloud_id);

      // Find cameras to add (in form but not in existing)
      const toAdd = formCameras.filter(
        (cloud_id) => !existingCloudIds.includes(cloud_id)
      );

      // Find cameras to remove (in existing but not in form)
      const toRemove = existingCameras.filter(
        (cam) => !formCameras.includes(cam.cloud_id || "")
      );

      // Add new cameras
      for (const cloud_id of toAdd) {
        try {
          await handleCameraOperation(() =>
            cameraService.createCamera(deviceId, { cloud_id })
          );
        } catch (error: any) {
          console.error(`Failed to add camera ${cloud_id}:`, error);
          messageService.warning(
            `Failed to bind camera ${cloud_id}: ${
              error.message || "Unknown error"
            }`
          );
        }
      }

      // Remove cameras
      for (const camera of toRemove) {
        try {
          if (camera.id) {
            await handleCameraOperation(() =>
              cameraService.deleteCamera(deviceId, camera.id)
            );
          }
        } catch (error: any) {
          console.error(`Failed to remove camera ${camera.cloud_id}:`, error);
          messageService.warning(
            `Failed to unbind camera ${camera.cloud_id || camera.id}: ${
              error.message || "Unknown error"
            }`
          );
        }
      }
    } catch (error: any) {
      console.error("Failed to sync cameras:", error);
      messageService.warning(
        `Failed to sync cameras: ${error.message || "Unknown error"}`
      );
    }
  };

  // Silent sync function - no messages or error handling to avoid disrupting UX
  const performSilentSync = async (orgId: string) => {
    try {
      // Add a small delay before syncing to allow the device to be fully created
      window.setTimeout(async () => {
        try {
          await syncAllDevices(orgId);
          console.log("Auto-sync completed successfully after device creation");

          if (onDeviceListRefresh) {
            window.setTimeout(() => {
              onDeviceListRefresh();
            }, 1000); // Give backend additional time to finish processing
          }
        } catch (error) {
          console.log("Auto-sync failed (silent):", error);
          // Fail silently - don't show error messages to user
        }
      }, 2000); // 2 second delay
    } catch (error) {
      // Fail silently
      console.log("Auto-sync setup failed (silent):", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate cameras - check for duplicate cloud_ids
    const cloudIds = cameras
      .map((cam) => cam.cloud_id?.trim())
      .filter((id) => id && id.length > 0);
    const uniqueCloudIds = new Set(cloudIds);
    if (cloudIds.length !== uniqueCloudIds.size) {
      messageService.error("Duplicate camera Cloud IDs are not allowed");
      return;
    }

    try {
      if (isEditMode) {
        // For update, all fields are optional - only include what we want to update
        const updateData: UpdateIQWatchDeviceParams = {
          name: form.name,
          timezone: form.timezone,
          // Include router_serial_number: send null if empty to remove it, or the value if provided
          router_serial_number: form.router_serial_number?.trim() || null,
        };
        await onSubmit(updateData);

        // Sync cameras after device update
        if (device) {
          await syncCameras(device.id);
        }

        messageService.success(`Device "${form.name}" updated successfully!`);
        onClose(); // Only close on successful update
      } else {
        // For create, include all required fields and optional router_serial_number
        const createData: CreateIQWatchDeviceParams = {
          name: form.name,
          iqwatch_device_id: form.iqwatch_device_id,
          org_id: form.org_id,
          site_id: form.site_id,
          timezone: form.timezone,
          // Include router_serial_number only if provided (optional field)
          ...(form.router_serial_number?.trim() && {
            router_serial_number: form.router_serial_number.trim(),
          }),
        };
        console.log("Modal: About to create device with data:", createData);
        await onSubmit(createData);
        console.log(
          "Modal: Device creation succeeded, showing success message"
        );
        messageService.success(`Device "${form.name}" created successfully!`);

        // Sync cameras after device creation
        // Wait for device to be created, then find it and sync cameras
        const camerasToBind = cameras.filter((cam) => cam.cloud_id?.trim());
        if (camerasToBind.length > 0) {
          try {
            // Wait a bit for the device to be fully created in the backend
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Try to find the device by fetching devices from the site
            // We'll search for the device with matching iqwatch_device_id
            try {
              const siteResponse = await getIQWatchDevicesBySite(
                form.site_id,
                form.org_id,
                1,
                100 // Get first 100 devices to find ours
              );

              const createdDevice = siteResponse.devices.find(
                (d) => d.device.iqwatch_device_id === form.iqwatch_device_id
              );

              if (createdDevice) {
                // Found the device, sync cameras
                await syncCameras(createdDevice.device.id);
                messageService.success(
                  `${camerasToBind.length} camera(s) bound successfully!`
                );
              } else {
                // Device not found yet, show info message
                messageService.info(
                  `Device created. Camera binding will be completed after sync. You can also edit the device to bind cameras.`
                );
              }
            } catch (findError) {
              console.error("Failed to find created device:", findError);
              messageService.info(
                `Device created. Camera binding will be completed after sync. You can also edit the device to bind cameras.`
              );
            }
          } catch (cameraError) {
            console.error(
              "Failed to sync cameras after creation:",
              cameraError
            );
            // Don't fail the whole operation if camera sync fails
            messageService.info(
              `Device created. Camera binding will be completed after sync. You can also edit the device to bind cameras.`
            );
          }
        }

        // Trigger automatic sync after successful device creation
        performSilentSync(form.org_id);

        onClose(); // Only close on successful creation
      }
    } catch (error: any) {
      console.error("Modal: Caught error in handleSubmit:", error);

      // Parse error message from server response
      let errorMessage = isEditMode
        ? "Failed to update device"
        : "Failed to create device";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show specific error messages for common cases
      if (
        errorMessage.toLowerCase().includes("already exists") ||
        errorMessage.toLowerCase().includes("duplicate")
      ) {
        errorMessage = `Device with ID "${form.iqwatch_device_id}" already exists. Please use a different device ID.`;
      }

      messageService.error(errorMessage);
      // Don't call onClose() here - keep the modal open so user can fix the issue
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] flex flex-col shadow-xl">
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? "Edit Device" : "Add Device"}
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Form Content - Scrollable */}
        <form
          id="device-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-4"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Device Name *
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                placeholder="Enter device name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MPPT ID *
              </label>
              <input
                type="text"
                required
                value={form.iqwatch_device_id}
                onChange={(e) =>
                  setForm({ ...form, iqwatch_device_id: e.target.value })
                }
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                placeholder="Enter MPPT ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Router Serial Number
                <span className="text-gray-500 text-xs ml-1">(Optional)</span>
              </label>
              <input
                type="text"
                value={form.router_serial_number || ""}
                onChange={(e) =>
                  setForm({ ...form, router_serial_number: e.target.value })
                }
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                placeholder="Enter router serial number (optional)"
              />
              {isEditMode && (
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to remove the router association
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone *
              </label>
              <Select
                isDisabled={loading}
                isSearchable
                required
                className="react-select-container"
                classNamePrefix="react-select"
                value={TIMEZONE_OPTIONS.find(
                  (opt) => opt.value === form.timezone
                )}
                onChange={(option) =>
                  setForm({ ...form, timezone: option ? option.value : "" })
                }
                options={TIMEZONE_OPTIONS}
                placeholder="Select timezone"
                styles={{
                  container: (base) => ({
                    ...base,
                    width: "100%",
                  }),
                  control: (base, state) => ({
                    ...base,
                    minHeight: "40px",
                    borderColor: state.isFocused ? "#22c55e" : "#d1d5db",
                    boxShadow: state.isFocused
                      ? "0 0 0 2px #22c55e33"
                      : undefined,
                  }),
                }}
              />
            </div>

            {/* Camera Binding Section */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  <Camera className="w-4 h-4 inline mr-2" />
                  Camera Binding
                  <span className="text-gray-500 text-xs ml-1">
                    (Optional, up to 4 cameras)
                  </span>
                </label>
                {cameras.length < 4 && (
                  <button
                    type="button"
                    onClick={addCameraField}
                    disabled={loading || loadingCameras}
                    className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 disabled:opacity-50"
                  >
                    <Plus className="w-3 h-3" />
                    Add Camera
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {cameras.map((camera, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <input
                          type="text"
                          value={camera.cloud_id}
                          onChange={(e) =>
                            updateCameraCloudId(index, e.target.value)
                          }
                          disabled={loading || loadingCameras}
                          placeholder={`Camera ${
                            index + 1
                          } Cloud ID (optional)`}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
                        />
                      </div>
                      {cameras.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCameraField(index)}
                          disabled={loading || loadingCameras}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                          title="Remove camera"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {loadingCameras && (
                <p className="text-xs text-gray-500 mt-2">
                  Loading camera information...
                </p>
              )}
              {isEditMode && existingCameras.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {existingCameras.length} camera(s) currently bound. Leave
                  Cloud ID empty to remove binding.
                </p>
              )}
            </div>
          </div>
        </form>
        {/* Footer with buttons - Fixed */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-white rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="device-form"
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {loading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update Device"
              : "Add Device"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddIQWatchDeviceModal;
